import { ChevronRight } from 'lucide-react';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import type { GapAnalysisSummary } from '~/features/catalog-operations/types/catalog-operations.types';
import { cn } from '~/utils/cn';

import { formatCount } from './gap-analysis.utils';
import { GapLegend } from './gap-legend';

interface Props {
  summary: GapAnalysisSummary;
}

/** One filled segment of a rail, widened proportionally against the larger of
 *  the two sides so both rails share a single scale. */
function RailSegment({
  widthPercent,
  className,
  tooltip,
  testId,
}: {
  widthPercent: number;
  className: string;
  tooltip: string;
  testId: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          data-testid={testId}
          className={cn('h-7 min-w-[3px] cursor-help rounded', className)}
          style={{ width: `${String(widthPercent)}%` }}
        />
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

function Rail({
  label,
  children,
  testId,
}: {
  label: string;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <div
      data-testid={testId}
      className="grid grid-cols-1 items-center gap-x-3.5 gap-y-1.5 sm:grid-cols-[122px_minmax(0,1fr)]"
    >
      <span className="text-muted-foreground text-xs font-medium sm:text-right">{label}</span>
      <div className="flex min-h-7 items-center gap-0.5">{children}</div>
    </div>
  );
}

/**
 * Before and after on one shared scale, so the size of what enrichment added is
 * read from the geometry rather than from comparing two numbers. An empty
 * baseline renders as a dashed rail — the strongest version of the story.
 * Revealed on demand from the control below the attribute table.
 */
export function GapComparisonRails({ summary }: Props) {
  const { baselineAttributeCount, enrichedAttributeCount, netNewCount, restatedCount } = summary;
  const scale = Math.max(baselineAttributeCount, enrichedAttributeCount, 1);
  const widthOf = (value: number) => (value / scale) * 100;

  // A carried-over segment only makes sense once the retailer had something to
  // carry over; with an empty baseline the after-rail is purely what we added.
  const carriedOver = baselineAttributeCount > 0 && restatedCount > 0 ? baselineAttributeCount : 0;

  const growthNote =
    baselineAttributeCount > 0
      ? `Enrichment left this product with ${String(Math.round((enrichedAttributeCount / baselineAttributeCount) * 10) / 10)}× the attributes it had before.`
      : 'This product had no attributes at all before enrichment.';

  return (
    <Collapsible className="border-b" data-testid="gap-analysis-rails-section">
      <CollapsibleTrigger
        data-testid="gap-analysis-rails-toggle"
        className="hover:bg-muted/50 text-muted-foreground hover:text-foreground group flex w-full cursor-pointer items-center gap-2 px-5 py-3 text-left text-[12.5px] font-medium"
      >
        <ChevronRight
          className="size-3.5 transition-transform group-data-[state=open]:rotate-90"
          aria-hidden="true"
        />
        Attribute count before and after enrichment
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="bg-muted/40 border-t p-5">
          <p className="text-muted-foreground text-xs" data-testid="gap-analysis-rails-growth-note">
            {growthNote}
          </p>

          <div className="mt-3.5 flex flex-col gap-3">
            <Rail label="Before enrichment" testId="gap-analysis-rails-before">
              {baselineAttributeCount > 0 ? (
                <>
                  <RailSegment
                    widthPercent={widthOf(baselineAttributeCount)}
                    className="bg-muted-foreground/15 border"
                    tooltip={`Before enrichment: ${formatCount(baselineAttributeCount)} attributes`}
                    testId="gap-analysis-rails-before-segment"
                  />
                  <span
                    className="text-foreground ml-2 text-xs font-semibold tabular-nums"
                    data-testid="gap-analysis-rails-before-value"
                  >
                    {formatCount(baselineAttributeCount)}
                  </span>
                </>
              ) : (
                <>
                  <span
                    className="text-muted-foreground flex h-7 flex-1 items-center rounded-md border border-dashed pl-3 text-xs"
                    data-testid="gap-analysis-rails-before-empty"
                  >
                    Nothing described this product
                  </span>
                  <span
                    className="text-foreground ml-2 text-xs font-semibold tabular-nums"
                    data-testid="gap-analysis-rails-before-value"
                  >
                    0
                  </span>
                </>
              )}
            </Rail>

            <Rail label="After enrichment" testId="gap-analysis-rails-after">
              {carriedOver > 0 ? (
                <RailSegment
                  widthPercent={widthOf(carriedOver)}
                  className="bg-muted-foreground/15 border"
                  tooltip={`${formatCount(carriedOver)} already there before enrichment`}
                  testId="gap-analysis-rails-after-had-segment"
                />
              ) : null}
              {restatedCount > 0 ? (
                <RailSegment
                  widthPercent={widthOf(restatedCount)}
                  className="bg-muted-foreground"
                  tooltip={`${formatCount(restatedCount)} restated — same meaning as existing content`}
                  testId="gap-analysis-rails-after-restated-segment"
                />
              ) : null}
              {netNewCount > 0 ? (
                <RailSegment
                  widthPercent={widthOf(netNewCount)}
                  className="bg-success"
                  tooltip={`${formatCount(netNewCount)} new attributes`}
                  testId="gap-analysis-rails-after-added-segment"
                />
              ) : null}
              <span
                className="text-success ml-2 text-xs font-semibold tabular-nums"
                data-testid="gap-analysis-rails-after-value"
              >
                {formatCount(enrichedAttributeCount)}
              </span>
            </Rail>
          </div>

          <GapLegend
            testId="gap-analysis-rails-legend"
            className="mt-3 sm:pl-[136px]"
            items={[
              ...(baselineAttributeCount > 0
                ? ([{ tone: 'had', label: 'Already there' }] as const)
                : []),
              ...(restatedCount > 0 ? ([{ tone: 'restated', label: 'Restated' }] as const) : []),
              { tone: 'added', label: 'New' },
            ]}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
